/**
 * CommissionGoalChart — one rep's commission year as a line (Sep 28, 2026).
 *
 * Greg picked this shape ("Trajectory", variant B of three) for tracking each
 * team member's commissions for the year and where their activity is
 * projecting them. The solid line is money PAID inside the crew competition
 * year (same engine as the Paid tab / My Business band, sliced by
 * lib/commissionGoals.commissionYearSlice), the dashed line is where the last
 * 30 days of points carry them, against the goal line and the tier rungs.
 * Below it: the rolling-30 meters against the goal's recipe, how the pace has
 * moved week to week, and what the next window needs.
 *
 * Three mounts, one component:
 *   • Home (rep's own view): the host passes `member` + the shared `core`
 *     snapshot — no second money fetch. Goal is read-only here.
 *   • 1-on-1 Mode (manager + rep together): `editable` — the component fetches
 *     the rep's roster row itself, runs the same snapshot hook for the rep
 *     (RLS: a TL/ASM/leadership session can read the rep's deals, ledger rows
 *     and rate), and shows the goal picker that writes crew_goals.
 *   • My Pay (Oct 19, 2026, the rep's own page): `selfEdit` — a slider to try
 *     any income target, live "what it takes" in apps / claims / builds every
 *     30 days, and Save as my goal (crew_goals policy goals_write_self_year).
 *     Greg: "so they can play with those numbers themselves and dream and set
 *     goals". The chart, verdict and meters follow the slider while it moves;
 *     nothing is saved until they tap Save. Their TL/ASM still sees (and can
 *     change) the same goal in the 1-on-1.
 *
 * Money is never re-derived here. Activity comes from crew_daily_points (the
 * leaderboard's feed). All the math lives in lib/commissionGoals.ts.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import type { TeamMember } from "../../lib/types";
import { todayET } from "../../lib/businessSnapshot";
import { ratesMapFromRows } from "../../lib/commissionRates";
import { useBusinessSnapshot, type CoreSnapshot } from "../home/useBusinessSnapshot";
import {
  DEFAULT_GOAL_TIERS,
  GOAL_ROLES,
  VERDICT_LABEL,
  addDays,
  computeGoal,
  crewYearFor,
  daysBetween,
  downlineIds,
  fetchDailyPoints,
  fetchGoalTierConfig,
  fetchYearDollarGoalRow,
  fmtDayShort,
  pointsOf,
  recipeForPoints,
  rollingSeries,
  rungsFor,
  setYearDollarGoal,
  sumWindow,
  type DailyPointRow,
  type GoalCalc,
  type GoalTierConfig,
  type Verdict,
} from "../../lib/commissionGoals";

interface Props {
  memberId: string;
  /** Host-provided (home): the viewer's own roster row + the shared snapshot. */
  member?: TeamMember;
  core?: CoreSnapshot | null;
  coreError?: string | null;
  /** Manager mode (1-on-1): shows the goal picker. */
  editable?: boolean;
  managerId?: string | null;
  /** My Pay: the person sets their own goal with a slider (only when they are the viewer). */
  selfEdit?: boolean;
}

interface TargetInfo { id: string; name: string; role: string; hire: string | null }

const ROLE_LABEL: Record<string, string> = { restore_manager: "Restore manager", team_lead: "Team lead", asm: "ASM" };
const VERDICT_CLS: Record<Verdict, string> = {
  on: "bg-emerald-50 text-emerald-700",
  close: "bg-amber-50 text-amber-800",
  behind: "bg-rose-50 text-rose-700",
};

// Counts read "3" or "2.5" — split credit is 0.5, never round it away.
const half = (n: number) => (Number.isInteger(n) ? String(n) : (Math.round(n * 10) / 10).toFixed(1));
const money = (v: number) => "$" + Math.round(v).toLocaleString("en-US");
const k = (v: number) => {
  const a = Math.abs(v);
  if (a >= 999500) return "$" + (v / 1_000_000).toFixed(2).replace(/\.?0+$/, "") + "M";
  if (a < 1000) return "$" + Math.round(v);
  if (a < 100000) return "$" + (v / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  return "$" + Math.round(v / 1000) + "k";
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (ym: string) => MONTHS[Number(ym.slice(5, 7)) - 1] ?? ym;
const lastDayOfMonth = (ym: string) => {
  const y = Number(ym.slice(0, 4)), m = Number(ym.slice(5, 7));
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
};
const nextMonth = (ym: string) => {
  const y = Number(ym.slice(0, 4)), m = Number(ym.slice(5, 7));
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, "0")}`;
};
function monthsBetween(fromISO: string, toISO: string): string[] {
  const out: string[] = [];
  for (let ym = fromISO.slice(0, 7); ym <= toISO.slice(0, 7); ym = nextMonth(ym)) out.push(ym);
  return out;
}

export default function CommissionGoalChart({ memberId, member, core, coreError, editable, managerId, selfEdit }: Props) {
  const { member: viewer, isSuperUser } = useAuth();
  const today = useMemo(() => todayET(), []);
  const year = useMemo(() => crewYearFor(today), [today]);
  const hostProvidesCore = core !== undefined;

  // Self-fetch mode (1-on-1): the rep's roster row feeds the same snapshot
  // hook the home page runs for the viewer.
  const [target, setTarget] = useState<TeamMember | null>(member ?? null);
  const [targetErr, setTargetErr] = useState<string | null>(null);
  useEffect(() => {
    if (member) { setTarget(member); return; }
    let cancelled = false;
    setTarget(null);
    supabase.from("team_roster").select("*").eq("id", memberId).maybeSingle().then(({ data, error }) => {
      if (cancelled) return;
      if (error || !data) { setTargetErr(error?.message ?? "Team member not found"); return; }
      setTargetErr(null);
      setTarget(data as TeamMember);
    });
    return () => { cancelled = true; };
  }, [memberId, member?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const selfSnap = useBusinessSnapshot(hostProvidesCore ? null : target, viewer?.role ?? null, !!isSuperUser);
  const snap = hostProvidesCore ? (core ?? null) : selfSnap.core;
  const snapErr = hostProvidesCore ? (coreError ?? null) : (selfSnap.error ?? targetErr);

  // Activity + config + goal + rate.
  const [cfg, setCfg] = useState<GoalTierConfig>(DEFAULT_GOAL_TIERS);
  const [rows, setRows] = useState<DailyPointRow[]>([]);
  const [downline, setDownline] = useState<string[]>([]);
  const [goal, setGoal] = useState<number | null>(null);
  // who set the goal last and when (shown under the picker and on My Pay)
  const [goalMeta, setGoalMeta] = useState<{ by: string | null; at: string | null } | null>(null);
  const [rosterNames, setRosterNames] = useState<Record<string, string>>({});
  const [rate, setRate] = useState<number | null>(null);
  const [info, setInfo] = useState<TargetInfo | null>(null);
  const [actLoading, setActLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setActLoading(true);
      setLoadErr(null);
      try {
        const [tiers, rosterRes, ratesRes, g] = await Promise.all([
          fetchGoalTierConfig(),
          supabase.from("team_roster").select("id, full_name, role, team_lead_id, asm_id, hire_date, is_active").eq("is_active", true),
          supabase.rpc("visible_commission_rates"),
          fetchYearDollarGoalRow(memberId, year.start),
        ]);
        if (cancelled) return;
        if (rosterRes.error) throw new Error(rosterRes.error.message);
        const roster = (rosterRes.data ?? []) as TeamMember[];
        const me = roster.find((m) => m.id === memberId) ?? null;
        if (!me) throw new Error("Team member is not on the active roster");
        const down = downlineIds(me, roster);
        // 20 weekly windows of rolling-30 history = 170 days of daily rows.
        const daily = await fetchDailyPoints([memberId, ...down], addDays(today, -(19 * 7 + 29)), today);
        if (cancelled) return;
        setCfg(tiers);
        setDownline(down);
        setRows(daily);
        setGoal(g?.target ?? null);
        setGoalMeta(g ? { by: g.setBy, at: g.setAt } : null);
        setRosterNames(Object.fromEntries(roster.map((m) => [m.id, m.full_name])));
        setRate(ratesRes.error ? null : (ratesMapFromRows(ratesRes.data).get(memberId) ?? null));
        setInfo({ id: me.id, name: me.full_name, role: me.role, hire: me.hire_date ?? null });
      } catch (e) {
        if (!cancelled) setLoadErr(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setActLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [memberId, year.start, today, tick]);

  // Live: a goal picked at the 1-on-1 lands on the rep's home; new activity
  // moves the meters (debounced — the log is sparse, ~15 events a day).
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const bump = () => { if (timer) clearTimeout(timer); timer = setTimeout(() => setTick((t) => t + 1), 1500); };
    const ch = supabase
      .channel(`cgc-${memberId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "crew_goals", filter: `user_id=eq.${memberId}` }, bump)
      .on("postgres_changes", { event: "*", schema: "public", table: "crew_activity_log" }, bump)
      .subscribe();
    return () => { if (timer) clearTimeout(timer); supabase.removeChannel(ch); };
  }, [memberId]);

  const isLead = info?.role === "team_lead" || info?.role === "asm";
  const basisIds = useMemo(() => new Set(isLead ? [memberId, ...downline] : [memberId]), [isLead, memberId, downline]);

  // My Pay: only the person themselves gets the slider.
  const canSelfEdit = !!selfEdit && !!viewer && viewer.id === memberId;
  const [draft, setDraft] = useState<number | null>(null);
  const shownGoal = canSelfEdit && draft != null ? draft : goal;

  const calc = useMemo<GoalCalc | null>(() => {
    if (!info) return null;
    const own = sumWindow(rows, new Set([memberId]), today);
    const down = isLead ? sumWindow(rows, new Set(downline), today) : null;
    const paidYear = snap?.commissionYear.paidTotal ?? 0;
    const queued = snap ? snap.outlook.pendingQueue + snap.outlook.approvedAwaitingPay : 0;
    return computeGoal({ role: info.role, rate, own, downline: down, goal: shownGoal, paidYear, queued, year, todayISO: today, hireISO: info.hire }, cfg);
  }, [info, rows, memberId, isLead, downline, snap, rate, shownGoal, year, today, cfg]);

  const series = useMemo(() => rollingSeries(rows, basisIds, today, cfg), [rows, basisIds, today, cfg]);
  const rungs = useMemo(() => rungsFor(info?.role ?? "restore_manager", cfg), [info?.role, cfg]);

  // Goal picker (manager mode)
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  async function pickGoal(target: number) {
    if (!managerId) return;
    setSaving(true);
    setSaveErr(null);
    const prev = goal, prevMeta = goalMeta;
    setGoal(target > 0 ? target : null);
    setGoalMeta(target > 0 ? { by: managerId, at: new Date().toISOString() } : null);
    try {
      await setYearDollarGoal({ userId: memberId, yearStart: year.start, target, createdBy: managerId });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      setGoal(prev);
      setGoalMeta(prevMeta);
      setSaveErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function saveOwnGoal(target: number) {
    if (!canSelfEdit || !viewer) return;
    setSaving(true);
    setSaveErr(null);
    const prev = goal, prevMeta = goalMeta;
    setGoal(target > 0 ? target : null);
    setGoalMeta(target > 0 ? { by: viewer.id, at: new Date().toISOString() } : null);
    try {
      await setYearDollarGoal({ userId: memberId, yearStart: year.start, target, createdBy: viewer.id });
      setDraft(null);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      setGoal(prev);
      setGoalMeta(prevMeta);
      setSaveErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  if (info && !GOAL_ROLES.has(info.role)) return null;

  const paid = snap?.commissionYear.paidTotal ?? 0;
  const queued = snap ? snap.outlook.pendingQueue + snap.outlook.approvedAwaitingPay : 0;
  const moneyReady = !!snap && !snapErr;
  // Labelled with the crowning date (the first Friday of December, when the
  // next crew year starts) — the day the team knows as the finish line.
  const yearLabel = `${fmtDayShort(year.start)}, ${year.start.slice(0, 4)} → ${fmtDayShort(year.end)}, ${year.end.slice(0, 4)}`;
  const first = (info?.name ?? "").split(" ")[0] || "They";

  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 pt-4 pb-2 sm:px-5">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
            🎯 Commission goal · crew year {yearLabel}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            {editable && info && <span className="text-lg font-extrabold text-slate-900">{info.name}</span>}
            {info && (
              <span className="text-xs text-slate-500">
                {ROLE_LABEL[info.role] ?? info.role}
                {rate != null ? ` · ${Math.round(rate * 100)}%` : ""}
                {info.hire && info.hire > year.start ? ` · since ${fmtDayShort(info.hire)} (pro-rated)` : ""}
              </span>
            )}
            {calc?.verdict && shownGoal ? (
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${VERDICT_CLS[calc.verdict]}`}>
                {VERDICT_LABEL[calc.verdict]} for {k(shownGoal)}{canSelfEdit && draft != null && draft !== goal ? " · not saved" : ""}
              </span>
            ) : info && !actLoading ? (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">No goal set yet</span>
            ) : null}
          </div>
        </div>
        {!editable && !canSelfEdit && !goal && info && !actLoading && (
          <p className="max-w-xs text-xs text-slate-500">
            Set your goal on <Link to="/my-pay" className="font-semibold text-orange-700 underline">My Pay</Link>, where you can see what it takes, or with your TL or ASM at the Monday 1-on-1. The chart shows your pace either way.
          </p>
        )}
      </div>

      {/* Goal slider — My Pay, the person's own page */}
      {canSelfEdit && info && calc && (
        <SelfGoalPanel
          calc={calc}
          cfg={cfg}
          rungs={rungs}
          isLead={isLead}
          goal={goal}
          draft={draft}
          setDraft={setDraft}
          saving={saving}
          savedFlash={savedFlash}
          saveErr={saveErr}
          onSave={saveOwnGoal}
          meta={goalMeta ? { by: goalMeta.by === memberId ? "you" : (goalMeta.by ? rosterNames[goalMeta.by] ?? "your manager" : null), at: goalMeta.at } : null}
        />
      )}

      {/* Goal picker — manager mode */}
      {editable && info && calc && (
        <div className="mx-4 mb-2 rounded-lg border border-orange-200 bg-orange-50/60 p-3 sm:mx-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold text-slate-800">Goal for this crew year</span>
            <span className="text-[11px] text-slate-500">
              {isLead ? "TL/ASM rungs — own deals plus the team's" : `at ${Math.round((rate ?? cfg.rmBaseRate) * 100)}%`}
            </span>
            {savedFlash && <span className="text-[11px] font-bold text-emerald-700">Saved.</span>}
            {saveErr && <span className="text-[11px] text-rose-700">{saveErr}</span>}
            {!managerId && <span className="text-[11px] text-amber-700">Sign in as a manager to set it.</span>}
            {goal && goalMeta?.by ? (
              <span className="text-[11px] text-slate-500">
                Set by {goalMeta.by === memberId ? `${first} on My Pay` : (rosterNames[goalMeta.by] ?? "a manager")}{goalMeta.at ? ` · ${fmtStamp(goalMeta.at)}` : ""}
              </span>
            ) : null}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {rungs.map((r) => {
              const rec = recipeForPoints(calc.perPointYear > 0 ? r / calc.perPointYear : 0, cfg);
              const on = goal === r;
              return (
                <button
                  key={r}
                  type="button"
                  disabled={saving || !managerId}
                  onClick={() => pickGoal(r)}
                  className={`rounded-lg border px-3 py-1.5 text-left disabled:cursor-not-allowed disabled:opacity-60 ${on ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-800 hover:border-slate-400"}`}
                >
                  <div className="text-sm font-extrabold">{k(r)}</div>
                  <div className={`text-[10px] ${on ? "text-slate-300" : "text-slate-500"}`}>
                    {half(rec.apps)} apps · {half(rec.claims)} claims · {half(rec.builds)} builds / 30d{isLead ? " (team)" : ""}
                  </div>
                </button>
              );
            })}
            {goal ? (
              <button
                type="button"
                disabled={saving || !managerId}
                onClick={() => pickGoal(0)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-500 hover:border-slate-400 disabled:opacity-60"
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>
      )}

      {loadErr ? (
        <div className="mx-4 mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 sm:mx-5">
          Couldn't load activity: {loadErr}
        </div>
      ) : actLoading || !calc || !info ? (
        <div className="px-4 pb-5 pt-2 sm:px-5">
          <div className="h-5 w-48 animate-pulse rounded bg-slate-100" />
          <div className="mt-3 h-40 animate-pulse rounded-lg bg-slate-100" />
        </div>
      ) : (
        <>
          {snapErr && (
            <div className="mx-4 mb-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 sm:mx-5">
              Paid dollars couldn't load ({snapErr}); the pace and meters below are from activity only.
            </div>
          )}

          {/* KPI tiles */}
          <div className="grid grid-cols-2 gap-2 px-4 pb-2 sm:px-5 md:grid-cols-4">
            <Kpi label="Paid this year" value={moneyReady ? k(paid) : "—"} detail={moneyReady ? (queued > 0 ? `+ ${k(queued)} queued on the board` : "nothing queued") : "loading…"} />
            <Kpi
              label="Pace (rolling 30)"
              value={<>{k(calc.pace)}<span className="text-xs font-semibold text-slate-400">/yr</span></>}
              detail={`${half(calc.basis === "team" ? calc.teamPts : calc.ownPts)} pts this window${calc.recipe ? ` · needs ${half(calc.recipe.pts)}` : ""}`}
            />
            <Kpi
              label={`Est. by ${fmtDayShort(year.end)}`}
              value={moneyReady ? k(calc.est) : "—"}
              detail={shownGoal ? (moneyReady ? (calc.est >= shownGoal ? `${k(calc.est - shownGoal)} over goal` : `${k(shownGoal - calc.est)} short of goal`) : "loading…") : `${calc.daysLeft} days left`}
            />
            <Kpi
              label="Where the goal says you'd be"
              value={calc.expected != null ? k(calc.expected) : "—"}
              detail={calc.expected != null && moneyReady ? (paid + queued >= calc.expected ? "ahead of the line" : "behind the line") : shownGoal ? "loading…" : "no goal yet"}
            />
          </div>

          {/* The chart */}
          <TrajectoryChart
            year={year}
            today={today}
            goal={shownGoal}
            rungs={rungs}
            paidByMonth={snap?.commissionYear.paidByMonth ?? {}}
            paid={moneyReady ? paid : 0}
            queued={moneyReady ? queued : 0}
            est={moneyReady ? calc.est : calc.pace * (calc.daysLeft / 365)}
            paceStart={calc.paceStart}
            moneyReady={moneyReady}
          />

          {/* Meters + sparkline */}
          <div className="grid gap-4 px-4 pb-4 sm:px-5 md:grid-cols-2">
            <div className="space-y-3">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                Rolling 30 · {calc.basis === "team" ? "you + your team" : "your activity"}
                {calc.recipe ? " vs the goal's recipe" : ""}
              </div>
              <Meter label="Apps" sub="last 30 days" value={calc.basisCounts.apps} target={calc.recipe?.apps ?? null} />
              <Meter label="Claims" sub="last 30 days" value={calc.basisCounts.claims} target={calc.recipe?.claims ?? null} />
              <Meter label="Builds" sub="on the calendar" value={calc.basisCounts.builds} target={calc.recipe?.builds ?? null} />
              <Meter label="Points" sub={`${cfg.points.app} · ${cfg.points.claim} · ${cfg.points.build}`} value={pointsOf(calc.basisCounts, cfg)} target={calc.recipe?.pts ?? null} />
              {calc.basis === "team" && (
                <p className="text-[11px] text-slate-500">
                  Your own {half(calc.ownPts)} pts count at your rate ({k(calc.ownPace)}/yr); the team's {half(calc.teamPts - calc.ownPts)} pts at the {info.role === "asm" ? "4%" : "1%"} override ({k(calc.overridePace)}/yr).
                </p>
              )}
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Rolling-30 points, week by week</div>
                <Sparkline series={series} target={calc.recipe?.pts ?? null} />
              </div>
              <div className="rounded-lg bg-orange-50 p-3 text-[13px] leading-relaxed text-slate-800">
                {whatItTakes(calc, shownGoal, first, isLead, cfg)}
              </div>
            </div>
          </div>

          {/* Table view — every number reachable without the chart */}
          <details className="border-t border-slate-200">
            <summary className="cursor-pointer px-4 py-2.5 text-xs font-bold text-slate-500 hover:bg-slate-50 sm:px-5">Table view · month by month</summary>
            <div className="overflow-x-auto px-4 pb-4 sm:px-5">
              <MonthTable year={year} today={today} paidByMonth={snap?.commissionYear.paidByMonth ?? {}} rows={rows} ids={basisIds} cfg={cfg} moneyReady={moneyReady} />
            </div>
          </details>
        </>
      )}
    </section>
  );
}

/** "Oct 19" for a timestamp (local display; the date a goal was last saved). */
function fmtStamp(ts: string): string {
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Slider bounds for a role: from $10k to twice the top rung (or 1.5× the current pace, if higher). */
function sliderRange(rungs: number[], pace: number): { min: number; max: number; step: number } {
  const top = Math.max(...rungs, 60000);
  const raw = Math.max(top * 2, pace * 1.5);
  const step = raw > 400000 ? 10000 : 5000;
  return { min: 10000, max: Math.ceil(raw / step) * step, step };
}

/**
 * My Pay's goal slider: try any yearly income, see the 30-day activity it takes
 * (the same recipe the 1-on-1 rungs use, scaled), save it as the goal.
 */
function SelfGoalPanel({ calc, cfg, rungs, isLead, goal, draft, setDraft, saving, savedFlash, saveErr, onSave, meta }: {
  calc: GoalCalc;
  cfg: GoalTierConfig;
  rungs: number[];
  isLead: boolean;
  goal: number | null;
  draft: number | null;
  setDraft: (v: number | null) => void;
  saving: boolean;
  savedFlash: boolean;
  saveErr: string | null;
  onSave: (target: number) => void;
  meta: { by: string | null; at: string | null } | null;
}) {
  const range = sliderRange(rungs, calc.pace);
  const start = Math.min(range.max, Math.max(range.min, Math.round((goal ?? rungs[1] ?? rungs[0] ?? 90000) / range.step) * range.step));
  const value = draft ?? start;
  const rec = recipeForPoints(calc.perPointYear > 0 ? value / calc.perPointYear : 0, cfg);
  const dirty = draft != null && draft !== goal;
  const who = isLead ? "you and your team" : "you";
  return (
    <div className="mx-4 mb-2 rounded-lg border border-orange-200 bg-orange-50/60 p-3 sm:mx-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-xs font-bold text-slate-800">🎯 Set your goal for this crew year</span>
        <span className="text-2xl font-extrabold text-slate-900">{money(value)}</span>
      </div>
      <input
        type="range"
        min={range.min}
        max={range.max}
        step={range.step}
        value={value}
        onChange={(e) => setDraft(Number(e.target.value))}
        aria-label="Yearly income goal"
        className="mt-2 w-full accent-orange-600"
      />
      <div className="flex justify-between text-[10px] text-slate-400">
        <span>{k(range.min)}</span>
        <span>{k(range.max)}</span>
      </div>
      <div className="mt-2 rounded-lg bg-white px-3 py-2 text-[13px] leading-relaxed text-slate-800">
        To earn <b>{money(value)}</b> a year, {who} need about{" "}
        <b>{half(rec.apps)} apps</b>, <b>{half(rec.claims)} claims</b> and <b>{half(rec.builds)} builds</b> every 30 days
        {" "}({half(rec.pts)} points). That's about <b>{k(value / 12)} a month</b>.
        {calc.pace > 0 && <> Your last 30 days are running at <b>{k(calc.pace)} a year</b>.</>}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {rungs.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setDraft(r)}
            className={`rounded-lg border px-2.5 py-1 text-xs font-bold ${value === r ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`}
          >
            {k(r)}
          </button>
        ))}
        <span className="flex-1" />
        {dirty && (
          <button type="button" onClick={() => setDraft(null)} disabled={saving}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-slate-400 disabled:opacity-60">
            Reset
          </button>
        )}
        {goal != null && !dirty && (
          <button type="button" onClick={() => { if (window.confirm("Clear your goal for this crew year?")) onSave(0); }} disabled={saving}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-500 hover:border-slate-400 disabled:opacity-60">
            Clear goal
          </button>
        )}
        <button type="button" onClick={() => onSave(value)} disabled={saving || (!dirty && goal === value)}
          className="rounded-lg bg-orange-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-orange-700 disabled:opacity-50">
          {saving ? "Saving…" : goal == null ? "Save as my goal" : dirty ? "Save new goal" : "Saved"}
        </button>
      </div>
      <div className="mt-1.5 text-[11px] text-slate-500">
        {savedFlash ? <span className="font-bold text-emerald-700">Saved. </span> : null}
        {saveErr ? <span className="text-rose-700">{saveErr} </span> : null}
        {goal != null && meta?.by
          ? `Your goal is ${money(goal)}, set by ${meta.by}${meta.at ? ` on ${fmtStamp(meta.at)}` : ""}. Your TL or ASM sees it at the 1-on-1 and can change it there.`
          : "Move the slider to see what any goal takes. Nothing is saved until you tap Save. Your TL or ASM sees your goal at the 1-on-1."}
      </div>
    </div>
  );
}

function Kpi({ label, value, detail }: { label: string; value: React.ReactNode; detail: string }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2.5">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-0.5 text-xl font-extrabold text-slate-900">{value}</div>
      <div className="text-[11px] text-slate-500">{detail}</div>
    </div>
  );
}

function Meter({ label, sub, value, target }: { label: string; sub: string; value: number; target: number | null }) {
  const pct = target && target > 0 ? Math.min(100, (value / target) * 100) : 0;
  return (
    <div className="grid grid-cols-[64px_1fr_86px] items-center gap-2.5">
      <div className="text-sm font-bold text-slate-900">
        {label}
        <div className="text-[10px] font-semibold text-slate-400">{sub}</div>
      </div>
      <div className="relative h-2.5 rounded-full bg-orange-100">
        {target ? <div className="h-full rounded-full bg-orange-500" style={{ width: `${pct}%` }} /> : <div className="h-full w-0" />}
        {target && value > target ? (
          <div className="absolute -top-1 h-[18px] w-0.5 rounded bg-slate-900" style={{ left: `${(target / value) * 100}%` }} title="the goal's target" />
        ) : null}
      </div>
      <div className="text-right text-sm font-extrabold tabular-nums text-slate-900">
        {half(value)}
        <div className="text-[10px] font-semibold text-slate-400">{target ? `target ${half(target)}` : "no goal"}</div>
      </div>
    </div>
  );
}

function Sparkline({ series, target }: { series: Array<{ end: string; pts: number }>; target: number | null }) {
  const w = 340, h = 96, l = 8, r = 8, t = 16, b = 18;
  if (series.length < 2) return null;
  const vals = series.map((s) => s.pts);
  const mx = Math.max(target ?? 0, ...vals, 1) * 1.15;
  const sx = (i: number) => l + (i / (vals.length - 1)) * (w - l - r);
  const sy = (v: number) => t + (h - t - b) - (v / mx) * (h - t - b);
  const d = vals.map((v, i) => `${i ? "L" : "M"}${sx(i).toFixed(1)},${sy(v).toFixed(1)}`).join(" ");
  const last = vals[vals.length - 1] ?? 0;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="block h-auto w-full" role="img" aria-label="Rolling-30 points by week">
      {target != null && target > 0 && (
        <>
          <line x1={l} x2={w - r} y1={sy(target)} y2={sy(target)} stroke="#f97316" strokeWidth="1" />
          <text x={w - r} y={sy(target) - 4} textAnchor="end" fontSize="10" fontWeight="700" fill="#475569">goal {half(target)} pts</text>
        </>
      )}
      <path d={`${d} L${sx(vals.length - 1)},${sy(0)} L${sx(0)},${sy(0)} Z`} fill="rgba(37,99,235,0.10)" />
      <path d={d} fill="none" stroke="#2563eb" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={sx(vals.length - 1)} cy={sy(last)} r="4" fill="#2563eb" stroke="#fff" strokeWidth="2" />
      <text x={sx(vals.length - 1) - 8} y={sy(last) - 8} textAnchor="end" fontSize="11" fontWeight="700" fill="#0f172a">{half(last)} pts</text>
      <text x={l} y={h - 4} fontSize="10" fill="#64748b">{fmtDayShort(series[0]!.end)}</text>
      <text x={w - r} y={h - 4} textAnchor="end" fontSize="10" fill="#64748b">{fmtDayShort(series[series.length - 1]!.end)}</text>
    </svg>
  );
}

function whatItTakes(calc: GoalCalc, goal: number | null, first: string, isLead: boolean, cfg: GoalTierConfig): React.ReactNode {
  const perPt = calc.perPointYear;
  if (!goal || !calc.recipe || !calc.gap) {
    return (
      <>
        No goal yet. At the current 30-day pace {first} is running <b>{k(calc.pace)} a year</b>. Each rolling-30 point is worth about <b>{money(perPt)} a year</b> of pace: a build is {cfg.points.build}, a claim {cfg.points.claim}, an app {cfg.points.app}.
      </>
    );
  }
  if (calc.pace >= goal) {
    return (
      <>
        At the current 30-day pace {first} is running <b>{k(calc.pace)} a year</b> against a {k(goal)} goal. Holding it is the whole job.
      </>
    );
  }
  const parts: React.ReactNode[] = [];
  if (calc.gap.apps > 0) parts.push(<b key="a">+{half(calc.gap.apps)} apps</b>);
  if (calc.gap.claims > 0) parts.push(<b key="c">+{half(calc.gap.claims)} claims</b>);
  if (calc.gap.builds > 0) parts.push(<b key="b">+{half(calc.gap.builds)} builds</b>);
  const who = isLead ? `${first}'s team (${first} included)` : first;
  return (
    <>
      To run at the {k(goal)} pace this window {who} needs{" "}
      {parts.length ? parts.map((p, i) => <span key={i}>{i ? ", " : ""}{p}</span>) : <b>a different mix</b>} on top of what's logged, or any mix worth <b>{half(calc.gap.pts)} more points</b>. One build moves the pace about <b>{k(perPt * cfg.points.build)}</b> a year, a claim {k(perPt * cfg.points.claim)}, an app {k(perPt * cfg.points.app)}.
    </>
  );
}

// ── The trajectory SVG ────────────────────────────────────────────────────

interface Station { day: string; label: string; paid: number | null; est: number | null; goalLine: number | null }

function TrajectoryChart({ year, today, goal, rungs, paidByMonth, paid, queued, est, paceStart, moneyReady }: {
  year: { start: string; end: string; days: number };
  today: string;
  goal: number | null;
  rungs: number[];
  paidByMonth: Record<string, number>;
  paid: number;
  queued: number;
  est: number;
  paceStart: string;
  moneyReady: boolean;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [tip, setTip] = useState<{ x: number; y: number; s: Station } | null>(null);

  const W = 760, H = 300, L = 56, R = 78, T = 22, B = 30;
  const pw = W - L - R, ph = H - T - B;
  const dayIdx = (iso: string) => Math.max(0, Math.min(year.days, daysBetween(year.start, iso)));
  const x = (iso: string) => L + (dayIdx(iso) / year.days) * pw;
  const ymax = Math.max(goal ?? 0, rungs[0] ?? 0, est, paid + queued, 1) * 1.08;
  const y = (v: number) => T + ph - (v / ymax) * ph;

  // Cumulative paid at each month end through today.
  const pts: Array<{ day: string; cum: number; label: string }> = [{ day: year.start, cum: 0, label: fmtDayShort(year.start) }];
  let cum = 0;
  for (const ym of monthsBetween(year.start, today)) {
    cum += paidByMonth[ym] ?? 0;
    const eom = lastDayOfMonth(ym);
    const day = eom > today ? today : eom;
    pts.push({ day, cum, label: `${monthLabel(ym)}${day === today ? " (to date)" : ""}` });
  }
  const linePath = pts.map((p, i) => `${i ? "L" : "M"}${x(p.day).toFixed(1)},${y(p.cum).toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L${x(pts[pts.length - 1]!.day).toFixed(1)},${y(0).toFixed(1)} L${x(year.start).toFixed(1)},${y(0).toFixed(1)} Z`;

  // Y ticks
  const step = [10000, 20000, 25000, 50000, 100000, 200000, 500000].find((s) => ymax / s <= 6) ?? 500000;
  const ticks: number[] = [];
  for (let v = 0; v <= ymax; v += step) ticks.push(v);

  // Month labels
  const months: Array<{ ym: string; mid: string }> = [];
  for (const ym of monthsBetween(year.start, addDays(year.end, -1))) {
    const s = `${ym}-01` > year.start ? `${ym}-01` : year.start;
    const e = `${nextMonth(ym)}-01` < year.end ? `${nextMonth(ym)}-01` : year.end;
    const span = daysBetween(s, e);
    if (span >= 10) months.push({ ym, mid: addDays(s, Math.floor(span / 2)) });
  }

  const goalLineAt = (day: string) => (goal ? goal * Math.min(1, Math.max(0, daysBetween(paceStart, day)) / Math.max(1, daysBetween(paceStart, year.end))) : null);
  const stations: Station[] = pts.map((p) => ({ day: p.day, label: p.label, paid: moneyReady ? p.cum : null, est: null, goalLine: goalLineAt(p.day) }));
  stations.push({ day: year.end, label: `${fmtDayShort(year.end)} (estimate)`, paid: null, est, goalLine: goal });

  function onMove(ev: React.PointerEvent<SVGRectElement>) {
    const el = wrapRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const px = ((ev.clientX - r.left) / r.width) * W;
    const d = ((px - L) / pw) * year.days;
    let best = stations[0]!;
    for (const s of stations) if (Math.abs(dayIdx(s.day) - d) < Math.abs(dayIdx(best.day) - d)) best = s;
    setTip({ x: Math.min(r.width - 176, Math.max(0, (x(best.day) / W) * r.width + 12)), y: Math.max(0, ev.clientY - r.top - 44), s: best });
  }

  const qTop = paid + queued;
  const estLabelDy = goal && Math.abs(y(est) - y(goal)) < 12 ? (est >= goal ? -10 : 14) : 4;

  return (
    <div ref={wrapRef} className="relative px-2 pb-1 sm:px-3">
      <svg viewBox={`0 0 ${W} ${H}`} className="block h-auto w-full" role="img" aria-label="Cumulative commission this crew year with projection">
        {ticks.map((v) => (
          <g key={v}>
            <line x1={L} x2={L + pw} y1={y(v)} y2={y(v)} stroke="#f1f5f9" strokeWidth="1" />
            <text x={L - 8} y={y(v) + 4} textAnchor="end" fontSize="11" fill="#94a3b8">{v === 0 ? "0" : `$${v / 1000}k`}</text>
          </g>
        ))}
        {rungs.filter((r) => r <= ymax).map((r) => {
          const isGoal = r === goal;
          return (
            <g key={r}>
              <line x1={L} x2={L + pw} y1={y(r)} y2={y(r)} stroke={isGoal ? "#0f172a" : "#cbd5e1"} strokeWidth={isGoal ? 1.5 : 1} />
              <text x={L + pw + 6} y={y(r) + 4} fontSize="11" fontWeight={isGoal ? 700 : 600} fill={isGoal ? "#0f172a" : "#94a3b8"}>{isGoal ? "Goal " : ""}${r / 1000}k</text>
            </g>
          );
        })}
        {goal && !rungs.includes(goal) && goal <= ymax && (
          <g>
            <line x1={L} x2={L + pw} y1={y(goal)} y2={y(goal)} stroke="#0f172a" strokeWidth="1.5" />
            <text x={L + pw + 6} y={y(goal) + 4} fontSize="11" fontWeight={700} fill="#0f172a">Goal ${goal / 1000}k</text>
          </g>
        )}
        {goal && <line x1={x(paceStart)} y1={y(0)} x2={x(year.end)} y2={y(goal)} stroke="#cbd5e1" strokeWidth="1" />}
        <line x1={x(today)} x2={x(today)} y1={T - 4} y2={T + ph} stroke="#e2e8f0" strokeWidth="1" />
        <text x={x(today)} y={T - 8} textAnchor="middle" fontSize="10" fontWeight="700" fill="#94a3b8">TODAY</text>
        {moneyReady && (
          <>
            <path d={areaPath} fill="rgba(37,99,235,0.10)" />
            <path d={linePath} fill="none" stroke="#2563eb" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            {queued > 0 && <line x1={x(today)} x2={x(today)} y1={y(paid)} y2={y(qTop)} stroke="#60a5fa" strokeWidth="4" strokeLinecap="round" />}
          </>
        )}
        <line x1={x(today)} y1={y(moneyReady ? qTop : 0)} x2={x(year.end)} y2={y(est)} stroke="#f97316" strokeWidth="2" strokeDasharray="6 5" strokeLinecap="round" />
        <circle cx={x(year.end)} cy={y(est)} r="4.5" fill="#fff" stroke="#f97316" strokeWidth="2" />
        <text x={x(year.end) + 8} y={y(est) + estLabelDy} fontSize="11" fontWeight="700" fill="#0f172a">est. {k(est)}</text>
        {moneyReady && pts.slice(1).map((p) => (
          <circle key={p.day} cx={x(p.day)} cy={y(p.cum)} r="4" fill="#2563eb" stroke="#fff" strokeWidth="2" />
        ))}
        {moneyReady && <text x={x(today) - 8} y={y(paid) - 10} textAnchor="end" fontSize="11" fontWeight="700" fill="#0f172a">{k(paid)} paid</text>}
        {months.map((m) => (
          <text key={m.ym} x={x(m.mid)} y={H - 10} textAnchor="middle" fontSize="11" fill="#94a3b8">{monthLabel(m.ym)}</text>
        ))}
        <line x1={L} x2={L + pw} y1={y(0)} y2={y(0)} stroke="#cbd5e1" />
        <rect x={L} y={T} width={pw} height={ph} fill="transparent" onPointerMove={onMove} onPointerLeave={() => setTip(null)} />
      </svg>
      {tip && (
        <div className="pointer-events-none absolute z-10 min-w-[160px] rounded-md bg-slate-900 px-2.5 py-2 text-xs text-white shadow-lg" style={{ left: tip.x, top: tip.y }}>
          <div className="font-bold">{tip.s.label}</div>
          {tip.s.paid != null && <div className="flex justify-between gap-3"><span><i className="mr-1.5 inline-block h-0.5 w-3 bg-blue-500 align-middle" />Paid to date</span><b>{money(tip.s.paid)}</b></div>}
          {tip.s.est != null && <div className="flex justify-between gap-3"><span><i className="mr-1.5 inline-block h-0.5 w-3 bg-orange-500 align-middle" />Projected</span><b>{money(tip.s.est)}</b></div>}
          {tip.s.goalLine != null && <div className="flex justify-between gap-3"><span><i className="mr-1.5 inline-block h-0.5 w-3 bg-slate-400 align-middle" />Goal line here</span><b>{money(tip.s.goalLine)}</b></div>}
        </div>
      )}
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 px-2 text-[11px] text-slate-500">
        <span><i className="mr-1.5 inline-block h-0.5 w-4 bg-blue-600 align-middle" />Paid (cumulative)</span>
        <span><i className="mr-1.5 inline-block w-4 border-t-2 border-dashed border-orange-500 align-middle" />Projected at current pace</span>
        <span><i className="mr-1.5 inline-block h-0.5 w-4 bg-slate-900 align-middle" />Goal</span>
        <span><i className="mr-1.5 inline-block h-px w-4 bg-slate-300 align-middle" />On-pace line</span>
      </div>
    </div>
  );
}

function MonthTable({ year, today, paidByMonth, rows, ids, cfg, moneyReady }: {
  year: { start: string; end: string; days: number };
  today: string;
  paidByMonth: Record<string, number>;
  rows: DailyPointRow[];
  ids: Set<string>;
  cfg: GoalTierConfig;
  moneyReady: boolean;
}) {
  const firstDay = rows.reduce((m, r) => (r.day < m ? r.day : m), today);
  let cum = 0;
  const lines = monthsBetween(year.start, today).map((ym) => {
    const p = paidByMonth[ym] ?? 0;
    cum += p;
    const inWindow = `${nextMonth(ym)}-01` > firstDay;
    const a = { apps: 0, claims: 0, builds: 0 };
    if (inWindow) for (const r of rows) { if (ids.has(r.user_id) && r.day.startsWith(ym)) { a.apps += r.apps; a.claims += r.claims; a.builds += r.builds; } }
    return { ym, p, cum, inWindow, a };
  });
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-[10px] uppercase tracking-wider text-slate-500">
          <th className="py-1.5 text-left font-bold">Month</th>
          <th className="py-1.5 text-right font-bold">Paid</th>
          <th className="py-1.5 text-right font-bold">Cumulative</th>
          <th className="py-1.5 text-right font-bold">Apps</th>
          <th className="py-1.5 text-right font-bold">Claims</th>
          <th className="py-1.5 text-right font-bold">Builds</th>
          <th className="py-1.5 text-right font-bold">Points</th>
        </tr>
      </thead>
      <tbody className="tabular-nums">
        {lines.map((l) => (
          <tr key={l.ym} className="border-t border-slate-100">
            <td className="py-1.5">{monthLabel(l.ym)} {l.ym.slice(0, 4)}</td>
            <td className="py-1.5 text-right">{moneyReady ? money(l.p) : "—"}</td>
            <td className="py-1.5 text-right">{moneyReady ? money(l.cum) : "—"}</td>
            <td className="py-1.5 text-right">{l.inWindow ? half(l.a.apps) : "—"}</td>
            <td className="py-1.5 text-right">{l.inWindow ? half(l.a.claims) : "—"}</td>
            <td className="py-1.5 text-right">{l.inWindow ? half(l.a.builds) : "—"}</td>
            <td className="py-1.5 text-right font-bold">{l.inWindow ? half(pointsOf(l.a, cfg)) : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
